// Wave UX-2.1 Item 1 (2026-05-23) — 외부 바이어 웹 i18n 인프라 (Pretendard 정합 lightweight).
// 4언어 ko/en/ar/ru. ARB 패턴 (Flutter Wave UX-1 Item 1.1) JSON 포팅.
//
// 사용:
//   1) main entry 에 <I18nProvider>...</I18nProvider> 감싸기
//   2) 컴포넌트에서:
//        var t = useT();
//        t('navAuctions') → "진행중 경매" / "Active Auctions" / ...
//        t('dashboardWelcome', { name: '홍길동' }) → "환영합니다, 홍길동님"
//
// Locale 우선순위:
//   1. localStorage.autodex_buyer_locale (수동 선택 보존)
//   2. navigator.language → 4언어 매핑
//   3. 'en' fallback
//
// RTL: locale === 'ar' 시 dir='rtl'. 전역(<html dir>) 설정은 **외부 바이어 도메인 한정** —
//      ERP 는 검차 셸 컨테이너(v2/simple/SimpleShell.js)만 뒤집는다 (신고 371).

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import koLocale from './locales/ko.json';
import enLocale from './locales/en.json';
import arLocale from './locales/ar.json';
import ruLocale from './locales/ru.json';
import { isBuyerDomain } from '../utils/buyerDomain';

var LOCALES = {
  ko: koLocale,
  en: enLocale,
  ar: arLocale,
  ru: ruLocale,
};

var SUPPORTED = ['ko', 'en', 'ar', 'ru'];
var FALLBACK = 'en';
var STORAGE_KEY = 'autodex_buyer_locale';

function resolveInitialLocale() {
  // 1. localStorage
  try {
    var saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.indexOf(saved) >= 0) return saved;
  } catch (e) {}
  // 2. navigator.language (e.g. 'en-US' → 'en')
  try {
    var nav = (navigator.language || '').toLowerCase().split('-')[0];
    if (SUPPORTED.indexOf(nav) >= 0) return nav;
  } catch (e) {}
  // 3. fallback
  return FALLBACK;
}

// ── ICU plural (최소 구현, 신고 371 결정 10) ──
// 앱 ARB 값을 그대로 이식하므로 `{count, plural, =0{…} =1{…} few{…} other{…}}` 를 해석해야 한다.
// 카테고리 규칙은 CLDR-lite — ko(other 단일) · en(one/other) · ru(one/few/many) · ar(zero/one/two/few/many/other).
function pluralCategory(locale, n) {
  if (locale === 'ko') return 'other';
  if (locale === 'en') return n === 1 ? 'one' : 'other';
  if (locale === 'ru') {
    var m10 = n % 10, m100 = n % 100;
    if (m10 === 1 && m100 !== 11) return 'one';
    if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'few';
    return 'many';
  }
  if (locale === 'ar') {
    if (n === 0) return 'zero';
    if (n === 1) return 'one';
    if (n === 2) return 'two';
    var a100 = n % 100;
    if (a100 >= 3 && a100 <= 10) return 'few';
    if (a100 >= 11 && a100 <= 99) return 'many';
    return 'other';
  }
  return 'other';
}

// s[start] 가 '{' 일 때 짝이 맞는 '}' 의 인덱스. 없으면 -1.
function matchBrace(s, start) {
  var depth = 0;
  for (var i = start; i < s.length; i++) {
    if (s[i] === '{') depth++;
    else if (s[i] === '}') { depth--; if (depth === 0) return i; }
  }
  return -1;
}

// '=1{…} few{…} other{…}' → { '=1': '…', few: '…', other: '…' }
function parsePluralOptions(body) {
  var opts = {};
  var i = 0;
  while (i < body.length) {
    while (i < body.length && /\s/.test(body[i])) i++;
    var open = body.indexOf('{', i);
    if (open < 0) break;
    var sel = body.slice(i, open).trim();
    var close = matchBrace(body, open);
    if (close < 0) break;
    if (sel) opts[sel] = body.slice(open + 1, close);
    i = close + 1;
  }
  return opts;
}

function pickPlural(locale, n, opts) {
  var exact = '=' + n;
  if (opts[exact] !== undefined) return opts[exact];
  var cat = pluralCategory(locale, n);
  if (opts[cat] !== undefined) return opts[cat];
  if (opts.other !== undefined) return opts.other;
  var keys = Object.keys(opts);
  return keys.length ? opts[keys[0]] : '';
}

function interpolate(template, vars, locale) {
  if (!template) return '';
  var out = template;
  // plural 블록을 먼저 접는다 — 선택된 분기 안의 {count} 는 아래 단순 치환이 처리한다.
  for (var guard = 0; guard < 8; guard++) {
    var m = /\{\s*(\w+)\s*,\s*plural\s*,/.exec(out);
    if (!m) break;
    var end = matchBrace(out, m.index);
    if (end < 0) break;
    var n = Number(vars ? vars[m[1]] : NaN);
    var body = out.slice(m.index + m[0].length, end);
    out = out.slice(0, m.index) + pickPlural(locale, isNaN(n) ? 0 : n, parsePluralOptions(body)) + out.slice(end + 1);
  }
  if (!vars) return out;
  return out.replace(/\{(\w+)\}/g, function(_, key) {
    return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : '{' + key + '}';
  });
}

var I18nContext = createContext({
  locale: FALLBACK,
  t: function(key) { return key; },
  setLocale: function() {},
  supportedLocales: SUPPORTED,
});

export function I18nProvider(props) {
  var [locale, setLocaleState] = useState(resolveInitialLocale);

  // RTL 자동 설정 + html lang attribute — **외부 바이어 도메인에서만** 전역으로 건다 (신고 371).
  //   ERP(autodex.kr)는 차량관리 화면까지 통째로 뒤집히면 안 되므로 검차 셸 컨테이너가 자기 dir 을 갖는다
  //   (v2/simple/SimpleShell.js). 경매 바이어 웹은 페이지 전체가 4언어라 기존 동작 유지.
  useEffect(function() {
    if (!isBuyerDomain()) return;
    try {
      document.documentElement.lang = locale;
      document.documentElement.dir = (locale === 'ar') ? 'rtl' : 'ltr';
    } catch (e) {}
  }, [locale]);

  var setLocale = function(next) {
    if (SUPPORTED.indexOf(next) < 0) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch (e) {}
    setLocaleState(next);
  };

  var t = useMemo(function() {
    var dict = LOCALES[locale] || LOCALES[FALLBACK];
    var fallbackDict = LOCALES[FALLBACK];
    return function(key, vars) {
      var template = dict[key];
      if (template === undefined) {
        // locale 누락 → fallback. fallback 도 없으면 key 그대로 (디버깅 친화).
        template = fallbackDict[key];
        if (template === undefined) return key;
      }
      return interpolate(template, vars, locale);
    };
  }, [locale]);

  var value = useMemo(function() {
    return { locale: locale, t: t, setLocale: setLocale, supportedLocales: SUPPORTED };
  }, [locale, t]);

  return <I18nContext.Provider value={value}>{props.children}</I18nContext.Provider>;
}

export function useT() {
  return useContext(I18nContext).t;
}

export function useLocale() {
  var ctx = useContext(I18nContext);
  return {
    locale: ctx.locale,
    setLocale: ctx.setLocale,
    supportedLocales: ctx.supportedLocales,
  };
}

// 테스트 / 외부 노출용 (직접 dict 접근)
export { LOCALES, SUPPORTED, FALLBACK };
